import React from "react"
import { AttachmentType, ShareAttachmentFragment } from "~/__generated__/graphql"
import { MessageAttachmentLiveSession } from "./message-attachment-live-session"
import { MessageAttachmentPoll } from "./message-attachment-poll"
import { MessageAttachmentQAndA } from "./message-attachment-qa"
import { MessageAttachmentAudio } from "./message-attachment-audio"
import { MessageAttachmentVideo } from "./message-attachment-video"
import { MessageAttachmentTrivia } from "./message-attachment-trivia"
import { MessageAttachmentEpisode } from "./message-attachment-episode"
import { MessageAttachmentQuote } from "./message-attachment-quote"
import { MessageAttachmentPredictions } from "./message-attachment-predictions"
import { UnsupportedAttachment } from "./unsupported-attachment"
import imageResize from "~/common/image-resize"
import { cn } from "~/common/cn"

interface MessageAttachmentsProps {
  attachments: ShareAttachmentFragment[] | null | undefined
  messageCreatedAt?: string
  playable?: boolean
  className?: string
}

export const MessageAttachments: React.FC<MessageAttachmentsProps> = ({
  attachments,
  messageCreatedAt,
  playable = true,
  className,
}) => {
  if (!attachments || attachments.length === 0) return null

  if (isMediaGrid(attachments))
    return <MediaGrid items={attachments} playable={playable} className={className} />

  const attachment = attachments[0]

  switch (attachment.attachmentType) {
    case AttachmentType.Image:
    case AttachmentType.AuraSticker:
      return (
        <img
          src={imageResize(attachment.imageUrl ?? "", {})}
          alt="attachment"
          className={`max-h-[400px] w-full max-w-[400px] rounded-md object-cover ${className}`}
        />
      )
    case AttachmentType.Giphy:
      return (
        <img
          src={attachment.giphy?.data?.images?.original_still?.url}
          alt="attachment"
          className={`max-h-[250px] w-full max-w-[400px] rounded-md object-cover ${className}`}
        />
      )
    case AttachmentType.Poll:
      return attachment.poll ? <MessageAttachmentPoll poll={attachment.poll} /> : null
    case AttachmentType.QAndA:
      return (
        <MessageAttachmentQAndA
          qAndAColor={attachment.qAndAColor}
          qAndAPrompt={attachment.qAndAPrompt}
        />
      )
    case AttachmentType.Audio:
      return <MessageAttachmentAudio voiceNoteUrl={attachment.voiceNoteUrl} playable={playable} />
    case AttachmentType.Video:
      return (
        <MessageAttachmentVideo
          muxPlaybackId={attachment.muxPlaybackId}
          transcodingState={attachment.transcodingState}
          aspectRatio={attachment.aspectRatio}
          playable={playable}
          className={className}
        />
      )
    case AttachmentType.Trivia:
      if (!attachment.trivia) return null

      return (
        <MessageAttachmentTrivia
          title={attachment.trivia.title}
          color={attachment.trivia.color}
          responseCount={attachment.trivia.responseCount}
        />
      )
    case AttachmentType.LiveSession:
    case AttachmentType.LiveStream:
      if (!attachment.liveSession) return <UnsupportedAttachment />
      return <MessageAttachmentLiveSession liveSession={attachment.liveSession} />
    case AttachmentType.Episode:
      if (!attachment.episode) return <UnsupportedAttachment />
      return <MessageAttachmentEpisode episode={attachment.episode} />
    case AttachmentType.Quote:
      if (!attachment.quoteText) return <UnsupportedAttachment />
      return (
        <MessageAttachmentQuote
          quoteText={attachment.quoteText}
          quoteAuthor={attachment.quoteAuthor}
        />
      )
    case AttachmentType.Predictions:
      if (!attachment.predictions) return <UnsupportedAttachment />
      return (
        <MessageAttachmentPredictions
          predictions={attachment.predictions}
          messageCreatedAt={messageCreatedAt}
        />
      )
    default:
      return <UnsupportedAttachment />
  }
}

const GRID_TYPES = [AttachmentType.Image, AttachmentType.Video]

// Several images or videos show together as a grid
export const isMediaGrid = (attachments: Pick<ShareAttachmentFragment, "attachmentType">[]) =>
  attachments.length > 1 && attachments.every((a) => GRID_TYPES.includes(a.attachmentType))
const GRID_TILE_COUNT = 4

// Tiles are absolutely positioned so their intrinsic size cannot stretch the grid tracks
const MediaGrid = ({
  items,
  playable,
  className,
}: {
  items: ShareAttachmentFragment[]
  playable: boolean
  className?: string
}) => {
  const hiddenCount = items.length - GRID_TILE_COUNT

  return (
    <div
      className={cn(
        "grid aspect-square w-full max-w-[400px] auto-rows-fr grid-cols-2 gap-0.5 overflow-hidden rounded-md",
        className
      )}
    >
      {items.slice(0, GRID_TILE_COUNT).map((item, index) => {
        const coveredByCount = index === GRID_TILE_COUNT - 1 && hiddenCount > 0
        const playableVideo = item.attachmentType === AttachmentType.Video && !coveredByCount
        return (
          <MediaTile
            key={item.id}
            imageUrl={playableVideo ? null : item.imageUrl}
            width={400}
            className={items.length === 3 && index === 0 ? "col-span-2" : undefined}
          >
            {playableVideo && (
              <div className="absolute inset-0 flex items-center justify-center">
                <MessageAttachmentVideo
                  muxPlaybackId={item.muxPlaybackId}
                  transcodingState={item.transcodingState}
                  playable={playable}
                  className="h-full rounded-none"
                />
              </div>
            )}
            {coveredByCount && <HiddenCount count={hiddenCount} />}
          </MediaTile>
        )
      })}
    </div>
  )
}

// Covers its frame with the image, under an optional overlay such as a play icon or a count
export const MediaTile = ({
  imageUrl,
  width,
  className,
  children,
}: {
  imageUrl: string | null | undefined
  width: number
  className?: string
  children?: React.ReactNode
}) => (
  <div className={cn("relative bg-neutral-200", className)}>
    {imageUrl && (
      <img
        src={imageResize(imageUrl, { width })}
        alt="attachment"
        className="absolute inset-0 h-full w-full object-cover"
      />
    )}
    {children}
  </div>
)

export const HiddenCount = ({ count }: { count: number }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-24 font-semibold text-white">
    +{count}
  </div>
)
